<template>
  <div class="feedback-buttons">
    <ButtonComponent
      class="button"
      :icon="feedback === 'positive' ? 'general.likeSolid' : 'general.like'"
      icon-size="xs"
      tooltip="Like"
      :class="{ selected: feedback === 'positive', like: true, 'not-clickable': feedback === 'positive' }"
      @click="giveFeedback('positive')"
      :style="feedback === 'positive' ? 'pointer-events: none;' : ''"
      data-testid="feedback-like"
    />
    <ButtonComponent
      class="button"
      :icon="feedback === 'negative' ? 'general.dislikeSolid' : 'general.dislike'"
      icon-size="xs"
      tooltip="Dislike"
      :class="{ selected: feedback === 'negative', dislike: true, 'not-clickable': feedback === 'negative' }"
      @click="giveFeedback('negative')"
      :style="feedback === 'negative' ? 'pointer-events: none;' : ''"
      data-testid="feedback-dislike"
    />
  </div>
</template>

<script setup lang="ts">
import { ButtonComponent } from '@/storybook';
import { useTrack, TrackEvents } from '@/composables/useTrack';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';

const props = defineProps<{
  feedback: null | 'positive' | 'negative';
  aiProvider?: string;
  additionalTrackingProps?: Record<string, any>;
}>();

const emit = defineEmits<{
  (e: 'feedback', type: 'positive' | 'negative' | null): void;
}>();

const { track } = useTrack();
const itemStore = useViewerItemStore();

function giveFeedback(type: 'positive' | 'negative') {
  if (props.feedback === type) return; // Prevent re-clicking selected

  const trackingData: Record<string, any> = {
    type,
    item_id: itemStore.id,
  };

  // Add AI provider if provided
  if (props.aiProvider) {
    trackingData.ai_provider = props.aiProvider;
  }

  // Merge additional tracking props (like smart_action_type for summaries)
  if (props.additionalTrackingProps) {
    Object.assign(trackingData, props.additionalTrackingProps);
  }

  // Use AI_FEEDBACK event for tracking AI provider feedback
  track(TrackEvents.AI_FEEDBACK, trackingData);

  // Emit feedback to parent after tracking
  emit('feedback', type);
}
</script>

<style scoped>
.feedback-buttons {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
.button {
  color: var(--slate-400);
  transition: color 0.2s, background 0.2s;
  border-radius: 50%;
  padding: 0.25rem;
  cursor: pointer;
}
.button:hover {
  background: var(--slate-100);
}
.button.selected.like {
  color: var(--green-500);
  background: var(--green-50);
}
.button.selected.dislike {
  color: var(--red-500);
  background: var(--red-50);
}
.button.not-clickable {
  cursor: default !important;
  pointer-events: none;
  background: inherit;
}
</style>
